import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { fetchApprovalCount } from '@/api/endpoints';
import { useAuth } from '@/stores/auth';
import { useRealtimeConnection } from '@/websocket/useRealtimeConnection';
import { ErrorBoundary } from './ErrorBoundary';
import { ModeBanner } from './ModeBanner';
import { StepUpDialog } from './StepUpDialog';

/** Channels every operator watches. Per-symbol market channels are subscribed
 *  by the screens that need them, once market data exists in Phase 5. */
const BASE_CHANNELS = ['portfolio', 'risk', 'alerts', 'system'];

const NAV = [
  { to: '/', label: 'Overview', end: true, permission: 'view:dashboard' },
  { to: '/positions', label: 'Positions', permission: 'view:dashboard' },
  { to: '/orders', label: 'Orders', permission: 'view:dashboard' },
  { to: '/approvals', label: 'Approvals', permission: 'view:dashboard', badge: true },
  { to: '/operations', label: 'Operations', permission: 'view:dashboard' },
  { to: '/controls', label: 'Controls', permission: 'view:dashboard' },
  { to: '/exchanges', label: 'Exchanges', permission: 'config:exchange_accounts' },
  { to: '/strategies', label: 'Strategies', permission: 'view:dashboard' },
  { to: '/risk', label: 'Risk limits', permission: 'view:dashboard' },
  { to: '/people', label: 'People', permission: 'danger:users' },
  { to: '/activity', label: 'Activity', permission: 'view:logs' },
  { to: '/audit', label: 'Audit trail', permission: 'view:audit' },
  { to: '/reports', label: 'Reports', permission: 'reports:export' },
  { to: '/settings', label: 'Your account', permission: 'view:dashboard' },
];

export function Layout() {
  const can = useAuth((s) => s.can);
  useRealtimeConnection(BASE_CHANNELS);

  // Polled from the shell rather than the approvals screen, because the whole
  // point is that a waiting decision is visible from wherever you happen to be.
  // A blocked loss exit that nobody notices makes the backstops decorative.
  const [approvals, setApprovals] = useState<{ open: number; critical: boolean }>({
    open: 0,
    critical: false,
  });

  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      fetchApprovalCount()
        .then((data) => {
          if (!cancelled) {
            setApprovals({ open: data.open_count, critical: data.has_critical });
          }
        })
        .catch(() => {
          /* The badge is an aid, not a feature: a failed poll must not break the shell. */
        });
    };
    poll();
    const timer = window.setInterval(poll, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  return (
    <div className="shell">
      <aside className="rail">
        <div className="rail-brand">
          Hartos
          <small>Spot trading console</small>
        </div>
        <nav aria-label="Main">
          {NAV.filter((item) => can(item.permission)).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              {item.label}
              {item.badge && approvals.open > 0 ? (
                <span
                  className={`badge ${approvals.critical ? 'badge--critical' : ''}`}
                  data-testid="approvals-badge"
                  aria-label={`${approvals.open} awaiting a decision`}
                >
                  {approvals.open}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="rail-footer">
          Orders are placed only by enabled accounts. Disabling an account stops trading; it never
          sells.
        </div>
      </aside>

      <div className="main">
        <ModeBanner />
        <main className="content">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <StepUpDialog />
    </div>
  );
}
